import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogIn, ShieldAlert } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const AUTO_REDIRECT_SECONDS = 10;

/**
 * Oturum süresi dolduğunda kullanıcıyı bilgilendirir, bulunduğu sayfayı
 * hatırlayarak güvenli şekilde giriş ekranına yönlendirir.
 */
const SessionExpiredDialog = () => {
  const { sessionExpired, clearSessionExpired } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [seconds, setSeconds] = useState(AUTO_REDIRECT_SECONDS);

  const onAuthPage = location.pathname === "/auth";
  const open = sessionExpired && !onAuthPage;

  const goToLogin = () => {
    const target = `${location.pathname}${location.search}`;
    const safe = /^\/(?!\/)/.test(target) && target !== "/auth" ? target : "/dashboard";
    clearSessionExpired();
    navigate(`/auth?reason=expired&redirect=${encodeURIComponent(safe)}`, { replace: true });
  };

  const goHome = () => {
    clearSessionExpired();
    navigate("/", { replace: true });
  };

  // Giriş sayfasındaysak uyarıyı sessizce temizle.
  useEffect(() => {
    if (sessionExpired && onAuthPage) clearSessionExpired();
  }, [sessionExpired, onAuthPage, clearSessionExpired]);

  useEffect(() => {
    if (!open) {
      setSeconds(AUTO_REDIRECT_SECONDS);
      return;
    }
    const id = window.setInterval(() => setSeconds((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => window.clearInterval(id);
  }, [open]);

  useEffect(() => {
    if (open && seconds === 0) goToLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seconds]);

  return (
    <AlertDialog open={open}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <ShieldAlert className="h-6 w-6 text-primary" aria-hidden="true" />
          </div>
          <AlertDialogTitle className="text-center">{t("auth.sessionExpiredTitle")}</AlertDialogTitle>
          <AlertDialogDescription className="text-center">
            {t("auth.sessionExpiredDesc")}
            <span className="mt-2 block text-xs text-muted-foreground">
              {t("auth.sessionExpiredCountdown", { seconds })}
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="sm:justify-center gap-2">
          <Button variant="ghost" onClick={goHome} className="sm:w-auto">
            {t("auth.sessionExpiredGoHome")}
          </Button>
          <AlertDialogAction onClick={goToLogin} className="gap-2">
            <LogIn className="h-4 w-4" aria-hidden="true" />
            {t("auth.sessionExpiredLogin")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default SessionExpiredDialog;
